import React, { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { Image, Stack } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { ActionButton, PrimaryButton } from 'common/components/Buttons/v8';
import { DialogCard } from 'common/components/Cards';
import { Link } from 'common/components/Link';
import { Heading1, Heading2, Text } from 'common/components/Text';
import { useScreen } from 'common/hooks/useScreen';
import { ImageConstants } from 'common/ImageConstants';
import { getAssetPath } from 'common/utilities/AssetsPath';
import { pageDataService } from 'common/utilities/PageDataService';
import { logView } from 'common/utilities/telemetryHelpers';
import { AppStoreApple, AppStoreGoogle, AppStoreMicrosoft, MacOSDirectDownload } from 'common/utilities/URLConstants';
import { useLocalizedStrings } from 'i18n';

import { CopyLinkComponent } from './ShareDevice/CopyLinkComponent';
import { ScanQRComponent } from './ShareDevice/ScanQRComponent';

interface IAddDeviceContentProps {
  headingId: string;
  telemetryPrefix?: string;
  telemetryProps?: Record<string, string | null>;
  title: string;
}

interface IAddDeviceFooterProps {
  onCloseClick?: () => void;
  telemetryPrefix?: string;
  telemetryProps?: Record<string, string | null>;
  /** Use to override the secondary button */
  onRenderSecondaryButton?: () => JSX.Element;
}

interface IAddDeviceDialogProps {
  isOpen: boolean;
  onCloseClick?: () => void;
}

export const downloadDefenderTelemetryPrefix = 'DownloadDefender';

// i18next-extract-mark-ns-start devices

export const AddDeviceContent = ({ headingId, telemetryPrefix: prefix, telemetryProps, title }: IAddDeviceContentProps) => {
  const { t } = useLocalizedStrings('devices');
  const { isMobileScreen: isMobile } = useScreen();

  const appStoreImgWidth = 124; // number from Figma. App store banners have different heights.

  const telemetryPrefix = prefix ?? downloadDefenderTelemetryPrefix;

  const tokens = {
    wrapper: { childrenGap: 24, padding: isMobile ? undefined : '0 0 0 20px' },
    column: { childrenGap: 8, maxWidth: 160 },
  };

  return (
    <Stack tokens={{ childrenGap: isMobile ? 24 : 50, padding: '0 56px 24px' }}>
      <Stack.Item shrink={0}>
        <Heading1
          announce
          id={headingId}
          variant={isMobile ? 'Title1Strong' : 'LargeTitle'}
          nowrap={false}
          style={{ textAlign: 'center' }}
        >
          {title}
        </Heading1>
      </Stack.Item>
      <Stack horizontal={!isMobile} tokens={tokens.wrapper} horizontalAlign={isMobile ? 'center' : 'space-between'}>
        {/* 1st column */}
        <Stack tokens={tokens.column}>
          <Heading2 variant="Body1">{t('AddDevice.Dialog.Subtitle.QRCode')}</Heading2>
          <ScanQRComponent />
        </Stack>

        {/* 2nd column */}
        <Stack tokens={tokens.column}>
          <Heading2 variant="Body1">{t('AddDevice.Dialog.Subtitle.CopyLink')}</Heading2>
          <CopyLinkComponent telemetryProps={telemetryProps} />
        </Stack>

        {/* 3rd column */}
        <Stack tokens={tokens.column}>
          <Heading2 variant="Body1">{t('AddDevice.Dialog.Subtitle.AppStore')}</Heading2>
          <Stack.Item>
            <Link
              href={AppStoreApple}
              target="_blank"
              telemetryEventName={`${telemetryPrefix}AppleStoreLinkClick`}
              telemetryProps={telemetryProps}
            >
              <Image
                src={getAssetPath(ImageConstants.AppStore_Apple)}
                width={appStoreImgWidth}
                alt={t('AddDevice.Dialog.ImageAlt.Apple')}
              />
            </Link>
          </Stack.Item>
          <Stack.Item>
            <Link
              href={AppStoreGoogle}
              target="_blank"
              telemetryEventName={`${telemetryPrefix}GoogleStoreLinkClick`}
              telemetryProps={telemetryProps}
            >
              <Image
                src={getAssetPath(ImageConstants.AppStore_GooglePlay)}
                width={appStoreImgWidth}
                alt={t('AddDevice.Dialog.ImageAlt.Google')}
              />
            </Link>
          </Stack.Item>
          <Stack.Item>
            <Link
              href={AppStoreMicrosoft}
              target="_blank"
              telemetryEventName={`${telemetryPrefix}MicrosoftStoreLinkClick`}
              telemetryProps={telemetryProps}
            >
              <Image
                src={getAssetPath(ImageConstants.AppStore_Microsoft)}
                width={appStoreImgWidth}
                alt={t('AddDevice.Dialog.ImageAlt.Microsoft')}
              />
            </Link>
          </Stack.Item>
          <Stack.Item>
            <Text>
              <Trans
                i18nKey="AddDevice.Dialog.MacOSDirectDownload"
                components={{ downloadLink: <Link href={MacOSDirectDownload} /> }}
                t={t}
              />
            </Text>
          </Stack.Item>
        </Stack>
      </Stack>
    </Stack>
  );
};

export const AddDeviceFooter = (props: IAddDeviceFooterProps) => {
  const { onCloseClick, telemetryPrefix: prefix, telemetryProps, onRenderSecondaryButton } = props;
  const { t } = useLocalizedStrings('devices');
  const { isMobileScreen } = useScreen();

  const telemetryPrefix = prefix ?? downloadDefenderTelemetryPrefix;

  const secondaryButton = onRenderSecondaryButton ? (
    onRenderSecondaryButton()
  ) : (
    <ActionButton
      text={t('AddDevice.Dialog.Dismiss')}
      telemetryEventName={`${telemetryPrefix}_GotIt`}
      onClick={onCloseClick}
      telemetryProps={telemetryProps}
    />
  );

  return (
    <Stack horizontal={!isMobileScreen} horizontalAlign="center" verticalAlign="center" tokens={{ childrenGap: 8 }}>
      <PrimaryButton
        text={t('AddDevice.Dialog.DownloadDefender')}
        href={pageDataService.getDownloadLinks().link}
        telemetryEventName={`${telemetryPrefix}_DownloadDefender`}
        telemetryProps={telemetryProps}
      />
      {secondaryButton}
    </Stack>
  );
};

export const AddDeviceDialog = (props: IAddDeviceDialogProps) => {
  const { isOpen, onCloseClick } = props;
  const { t } = useLocalizedStrings('devices');

  const headingId = useId();

  const title = t('AddDevice.Dialog.Title');

  useEffect(() => {
    if (isOpen) {
      logView('AddDevice');
    }
  }, [isOpen]);

  return (
    <DialogCard
      isOpen={isOpen}
      onCloseClick={onCloseClick}
      disableFullScreenCard
      onRenderFooter={() => <AddDeviceFooter onCloseClick={onCloseClick} />}
      titleAriaId={headingId}
    >
      <AddDeviceContent headingId={headingId} title={title} />
    </DialogCard>
  );
};

// i18next-extract-mark-ns-stop devices
